import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { createdBy as principalName } from '~components/Job/AuditDetails/utils';
import { Ticket } from '~hooks/useTickets/models';

import {
  DismissFooter,
  DismissibleHoverCard,
  usePopoverAction,
} from '../Files/components/LoadsTable/components/AiReviewIndicator';

interface DuplicateTicketIndicatorProps {
  onPotentialDuplicateChange?: (potentialDuplicate: boolean) => Promise<void>;
  ticket: Ticket;
}

export function getDismissedDuplicateLabel(ticket: Ticket) {
  const dismissedBy = ticket.potentialDuplicateDismissedBy;

  return dismissedBy
    ? t('approvals.files.ticket_marked_not_a_duplicate_by', {
        name: principalName(dismissedBy),
      })
    : t('approvals.files.ticket_marked_not_a_duplicate');
}

export default function DuplicateTicketIndicator({
  onPotentialDuplicateChange,
  ticket,
}: DuplicateTicketIndicatorProps) {
  if (!ticket.isPotentialDuplicateFlagged) {
    return null;
  }

  if (!onPotentialDuplicateChange) {
    return <DuplicateLabel />;
  }

  return (
    <DismissibleHoverCard
      badge={<DuplicateLabel />}
      isPinnable
      renderContent={(close) => (
        <DuplicatePopoverContent
          close={close}
          onDismiss={() => onPotentialDuplicateChange(false)}
        />
      )}
    />
  );
}

function DuplicateLabel() {
  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        color: theme.palette.error.main,
        display: 'flex',
        gap: 0.25,
        minWidth: 0,
      })}
    >
      <Warning sx={{ fontSize: '11px', flexShrink: 0 }} />

      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {t('approvals.files.loads_table.duplicate')}
      </Typography>
    </Box>
  );
}

interface DuplicatePopoverContentProps {
  close: () => void;
  onDismiss: () => Promise<void>;
}

function DuplicatePopoverContent({ close, onDismiss }: DuplicatePopoverContentProps) {
  const { isRunning, run } = usePopoverAction(onDismiss, close);

  return (
    <Box sx={{ p: 2, maxWidth: 350, minWidth: 250 }}>
      <Typography
        sx={(theme) => ({ color: theme.brandV2.colors.treadBlack, fontWeight: 600 })}
      >
        {t('approvals.files.ticket_duplicate_title')}
      </Typography>

      <Typography
        variant="body2"
        sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
      >
        {t('approvals.files.ticket_duplicate_description')}
      </Typography>

      <DismissFooter
        buttonLabel={t('approvals.files.ticket_not_a_duplicate')}
        dataTestId="ticket-not-a-duplicate"
        isDismissing={isRunning}
        onDismiss={run}
        prompt={t('approvals.files.ticket_not_a_duplicate_prompt')}
      />
    </Box>
  );
}
